import { useTranslation } from 'react-i18next'

import { useMaintenance } from '@context/MaintenanceModeContext'
import { useSnackbar } from '@context/SnackbarContext'

import { ResponseError, isAlreadyFetchingAccessToken } from '@utils/ApiClient'
import {
  isForbiddenStatus,
  isNotFoundStatus,
  isServiceUnavailableStatus,
  isUnauthorizedStatus,
} from '@utils/httpStatus'

import { useLogout } from '@hooks/useLogout'

import {
  handleToastType,
  isInvalidPassword,
  isPspServiceCheckError,
  isSkipErrorCode,
  isSubscriptionCartForClientExists,
} from '../RouterToaster'
import {
  getChildErrors,
  getErrorResponseData,
  getPspServiceCheckErrorMessage,
  hasChildErrors,
  isIpApiCall,
} from './ErrorResponse'

interface ErrorHandler {
  errorHandler: (error: ResponseError) => Promise<void | null>
}

export const useErrorHandler = (): ErrorHandler => {
  const { t } = useTranslation()
  const { setMaintenance } = useMaintenance()
  const { logout } = useLogout()
  const { addSnackbar } = useSnackbar()

  const errorHandler = async (error: ResponseError) => {
    const title = t('Something went wrong! Please try again later')
    const responseData = getErrorResponseData(error)
    const { status, errorCode, isMaintenanceMode, maintenance, response } = responseData

    const supportEmail =
      response?.response?.data?.properties?.companyAndSupport?.SupportDto?.SupportEmail

    /**
     * https://jira.tickmill.com/browse/DL-6330
     */
    if (isIpApiCall(error)) {
      return null
    }

    // handle 401 UNAUTHORIZED - logout user (no token or invalid token)
    if (isUnauthorizedStatus(status)) {
      if (!isAlreadyFetchingAccessToken) {
        logout()
        return null
      }
    }

    // handle the 403 FORBIDDEN - show error message
    if (isForbiddenStatus(status)) {
      const errorMessage =
        responseData.response?.response?.data?.message ||
        t('errors.Access denied. You do not have permission to access this resource.')

      // this might be a CloudFlare 403?
      const isCloudflare403 =
        !responseData.response?.response?.data?.code &&
        !responseData.response?.response?.data?.message

      if (isCloudflare403) {
        addSnackbar.error({
          message: t(
            'errors.Access blocked by security system. Please try again later or contact support.'
          ),
        })
        return null
      }

      addSnackbar.error({ message: errorMessage })
      return null
    }

    /**
     * CAR-267 (https://jira.tickmill.com/browse/CAR-267)
     *
     * @description:
     * When error 404 is received by any call in the backend, do not throw toast error message for it. Each 404 will be handled properly wherever is needed.
     */
    if (isNotFoundStatus(status)) {
      return null
    }

    /**
     * CAR-634 (https://jira.tickmill.com/browse/CAR-634)
     *
     */
    if (isMaintenanceMode) {
      setMaintenance?.(maintenance)
    }
    if (isMaintenanceMode && isServiceUnavailableStatus(status)) {
      return null
    }

    // TODO: BE has a third-part service, which can't provide the normal "code", BE should fix it.
    if (isInvalidPassword(error)) {
      addSnackbar.error({ message: t('errors.Invalid password') })
      return null
    }

    if (isSubscriptionCartForClientExists(error)) {
      addSnackbar.error({
        message: t('errors.The selected item already exists in subscriptions cart'),
      })
      return null
    }

    if (isPspServiceCheckError(error)) {
      const toastMessage = getPspServiceCheckErrorMessage(error)
      addSnackbar.error({ message: toastMessage || t('errors.Something went wrong') })
      return null
    }

    if (isSkipErrorCode(error)) {
      return null
    }

    // for handle multiple exceptions (aggregate errors)
    if (hasChildErrors(error)) {
      const childErrors = getChildErrors(error)
      const parentMessage =
        responseData.response?.response?.data?.Message ||
        responseData.response?.response?.data?.message ||
        t('Multiple validation errors occurred')

      addSnackbar.error({
        message: parentMessage,
        subTitle: childErrors
          .filter((childError) => childError.Message)
          .map((childError) => `• ${childError.Message}`)
          .join('\n'),
      })
      return null
    }

    if (errorCode) {
      handleToastType(error, t, addSnackbar, supportEmail)
      return null
    }

    if (isMaintenanceMode) {
      return setMaintenance?.(maintenance)
    }

    addSnackbar.error({ message: title })
    return null
  }

  return { errorHandler }
}
